import { StandardConsumptionTypeEnum, UserRoles } from '@/shared/core';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import * as yup from 'yup';
import {
	CustomNumDomain,
	RubberTechMapResponse,
	UserRoleEnum,
	RubberOperationResponse,
	CustomRubberResponse,
	RubberCatalogResponse,
} from '@/shared/state/api/swagger';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { isChoppingOperation } from '@/widgets/tech-card/blocks/operations/lib/operations-utils';

const validateCount = (val: any) => !val || !isNaN(Number(val)) || /^-{1,}$/.test(String(val));

const creationValidationScheme = yup.object({
	created_at: yup.string().nullable(),
	custom_rubber: yup.object({
		name: yup.string().required().default(''),
		extra_name: yup.string().nullable().default(''),
		symbol: yup.string().required().default(''),
		material: yup.object({
			gost: yup.string().required().default(''),
			symbol: yup.string().required().default(''),
			sortament: yup.string().required().default(''),
		}),
	}),
	rubber_plan: yup.object({
		NR_grams: yup
			.mixed()
			.nullable()
			.test('NR_grams-create-validation', 'NR_grams-create-validation', (val) => {
				const currentValue = Number(String(val).replace(',', '.'));
				return !val || (!isNaN(currentValue) && currentValue > 0) || /^-{1,}$/.test(String(val));
			}),
		plan_count: yup.number().required(),
		list_number: yup.number().nullable().default(1),
		issuance_count: yup
			.mixed()
			.nullable()
			.test(
				'validity',
				'',
				(val) => !val || !isNaN(Number(String(val).replace(',', '.'))) || /^-{1,}$/.test(String(val)),
			),
		issuance_fio: yup.string().required(),
	}),
	rubber_num: yup.object({
		id: yup.number().default(0),
		num: yup.number().nullable().required(),
		gost: yup.string().default(''),
		number_of_melt: yup.string().default(''),
		date_of_manufacture: yup.string().default('').nullable(),
		o_v_or_date_of_manufacture: yup.string().required(),
		sortament: yup.string().default(''),
		symbol: yup.string().default(''),
	}),
});

const editValidationScheme = yup.object({
	created_at: yup.string().nullable(),
	number: yup
		.string()
		.min(3)
		.test('is-valid-number', 'number not valid', (val) => {
			const year = Number((val ?? '')?.trim().slice(0, 2));

			if (Number.isNaN(year)) return false;

			return new Date().getFullYear() % 100 >= year;
		}),
	symbol: yup
		.string()
		.test('len', 'Must be less than 2 characters', (val) => (val ? val?.length <= 2 : true))
		.notRequired(),
	count_gram: yup.number().nullable(),
	count_number: yup.number().nullable(),
	storage_at: yup
		.string()
		.nullable()
		.transform((value) => {
			if (value && dayjs(value).isValid()) {
				return dayjs(value).toISOString();
			}
			return value;
		}),
	custom_rubber: yup.object({
		name: yup.string().required().default(''),
		extra_name: yup.string().nullable().default(''),
		symbol: yup.string().required().default(''),
		approval_card: yup.string().nullable().default(''),
		extra_approval_card: yup.string().nullable().default(''),
		material: yup.object({
			gost: yup.string().required().default(''),
			symbol: yup.string().required().default(''),
			sortament: yup.string().required().default(''),
		}),
	}),
	rubber_plan: yup.object({
		NR_grams: yup
			.mixed()
			.nullable()
			.test('NR_grams-create-validation', 'NR_grams-create-validation', (val) => {
				const currentValue = Number(String(val).replace(',', '.'));
				return !val || (!isNaN(currentValue) && currentValue > 0) || /^-{1,}$/.test(String(val));
			}),
		count: yup.number().nullable(),
		fio: yup.string().nullable().default(''),
		issuance_count: yup
			.mixed()
			.nullable()
			.test(
				'validity',
				'',
				(val) => !val || !isNaN(Number(String(val).replace(',', '.'))) || /^-{1,}$/.test(String(val)),
			),
		return_count_grams: yup.string().nullable().default(''),
		return_fio: yup.string().nullable().default(''),
		list_number: yup.string().default('1'),
		tech_decision: yup.string().nullable().default(''),
		summary: yup.string().nullable(),
		product_releases: yup.array().of(
			yup.object({
				count: yup.string().nullable(),
				count_kg: yup.string().nullable(),
				number: yup.string().nullable(),
				fio: yup.string().nullable(),
			}),
		),
	}),
	rubber_num: yup.object({
		id: yup.number().default(0),
		num: yup.string().default(''),
		gost: yup.string().default(''),
		number_of_melt: yup.string().default(''),
		date_of_manufacture: yup.string().default('').nullable(),
		sortament: yup.string().default(''),
		symbol: yup.string().default(''),
		o_v_or_date_of_manufacture: yup.string().required(),
	}),

	rubber_operations: yup.array().of(
		yup.object().shape({
			step_id: yup.number().nullable(),
			user_id: yup.number().nullable(),
			date: yup.string().nullable(),
			rubber_tech_map_id: yup.number().nullable(),
			wasted: yup.mixed().nullable().test('validity', '', validateCount),
			fio: yup.string().nullable(),
			name: yup.string().nullable(),
			count_in_number: yup.mixed().nullable().test('validity', '', validateCount),
			count_out_number: yup
				.mixed()
				.nullable()
				.test('', 'count in number less then count out number', (val, ctx) => {
					if (!val) return true;

					if (
						(ctx.parent.count_in_number === '-' || ctx.parent.count_in_number === null) &&
						(val === '-' || !isNaN(Number(val)))
					)
						return true;
					if (val === '-') return true;
					if (!isNaN(Number(val)) && val !== 0) {
						if (isChoppingOperation(ctx.parent.name)) return true; // Допускаем превышение выходного количества для вырубной операции
						if (Number(val) <= Number(ctx.parent.count_in_number)) return true;
					}
					return false;
				}),
			id: yup.number().nullable(),
		}),
	),
	default_rubber_operations: yup.array().of(
		yup.object().shape({
			step_id: yup.number().nullable(),
			user_id: yup.number().nullable(),
			date: yup.string().nullable(),
			rubber_tech_map_id: yup.number().nullable(),
			wasted: yup.mixed().nullable().test('validity', '', validateCount),
			fio: yup.string().nullable(),
			name: yup.string().nullable(),
			count_in_number: yup.mixed().nullable().test('validity', '', validateCount),
			count_out_number: yup
				.mixed()
				.nullable()
				.test('', 'count in number less then count out number', (val, ctx) => {
					if (!val) return true;

					if (
						(ctx.parent.count_in_number === '-' || ctx.parent.count_in_number === null) &&
						(val === '-' || !isNaN(Number(val)))
					)
						return true;
					if (val === '-') return true;
					if (!isNaN(Number(val)) && val !== 0) {
						if (Number(val) <= Number(ctx.parent.count_in_number)) return true;
					}
					return false;
				}),
			id: yup.number().nullable(),
		}),
	),
	rubber_sgd_out: yup.array().of(
		yup.object({
			date: yup.string().nullable(),
			fio: yup.string().nullable().default(''),
			gram: yup.number().nullable().default(1),
			count: yup.number().nullable().default(1),
			certificate: yup.string().nullable().default(''),
		}),
	),
});

export interface RubberTechMapFormData
	extends Omit<RubberTechMapResponse, 'user_id' | 'id' | 'custom_rubber' | 'rubber_operations' | 'rubber_num'> {
	custom_rubber: Partial<RubberCatalogResponse> & Pick<CustomRubberResponse, 'rubber_catalog_id'>;
	rubber_operations?: RubberOperationResponse[];
	default_rubber_operations?: RubberOperationResponse[];
	custom_rubber_operations?: RubberOperationResponse[];
	rubber_num: Omit<CustomNumDomain, 'standard_consumption_type'> & {
		standard_consumption_type?: StandardConsumptionTypeEnum;
	};
}

export const useRubberFormConfig = (
	isCreationMode: boolean,
	userRole?: UserRoleEnum,
	cardInfo?: RubberTechMapResponse,
) => {
	const preparedEditValidationScheme = useMemo(
		() =>
			userRole === UserRoles.admin ? editValidationScheme.concat(creationValidationScheme) : editValidationScheme,
		[userRole],
	);

	const emptyObjectArray = (length: number) => {
		return new Array(length).fill({});
	};

	const sgdOutField = (length: number) => {
		switch (length) {
			case 0:
				return emptyObjectArray(3);
			default:
				// return [...(cardInfo?.rubber_sgd_out || []), ...emptyObjectArray(20 - (cardInfo?.rubber_sgd_out?.length ?? 0))];
				return [...(cardInfo?.rubber_sgd_out || []), cardInfo?.rubber_sgd_out?.length ?? 0];
		}
	};

	const initialValues = useMemo<RubberTechMapFormData>(
		() => ({
			...cardInfo,

			status: cardInfo?.status,
			number: cardInfo?.number || 0,
			created_at: cardInfo?.created_at,
			symbol: cardInfo?.symbol ?? 'Р',
			updated_at: cardInfo?.updated_at,
			custom_rubber: {
				symbol: cardInfo?.custom_rubber?.symbol,
				name: cardInfo?.custom_rubber?.name,
				approval_card: cardInfo?.custom_rubber?.approval_card,
				extra_symbol: cardInfo?.custom_rubber?.extra_symbol,
				extra_name: cardInfo?.custom_rubber?.extra_name,
				extra_approval_card: cardInfo?.custom_rubber?.extra_approval_card,
				standard_consumption_grams: cardInfo?.custom_rubber?.standard_consumption_grams,
				billet: cardInfo?.custom_rubber?.billet,
				weighed_amount: cardInfo?.custom_rubber?.weighed_amount,
				press_form: cardInfo?.custom_rubber?.press_form,
				thermostating_mandrel: cardInfo?.custom_rubber?.thermostating_mandrel,
				id: cardInfo?.custom_rubber?.id || 0,
				material: {
					gost: cardInfo?.custom_rubber?.custom_material?.gost,
					symbol: cardInfo?.custom_rubber?.custom_material?.symbol,
					sortament: cardInfo?.custom_rubber?.custom_material?.sortament,
					gost_na_sortament: cardInfo?.custom_rubber?.custom_material?.gost_na_sortament,
					material_catalog_id: cardInfo?.custom_rubber?.custom_material?.material_catalog_id,
				},
				rubber_catalog_id: cardInfo?.custom_rubber?.rubber_catalog_id || 0,
			},
			rubber_num: {
				...cardInfo?.rubber_num,
			},
			rubber_plan: {
				plan_count: cardInfo?.rubber_plan?.plan_count || 0,
				NR_grams: cardInfo?.rubber_plan?.NR_grams,
				issuance_count: cardInfo?.rubber_plan?.issuance_count,
				issuance_fio: cardInfo?.rubber_plan?.issuance_fio,
				list_number: cardInfo?.rubber_plan?.list_number,
				tech_decision: cardInfo?.rubber_plan?.tech_decision,
				summary: cardInfo?.rubber_plan?.summary,
				id: cardInfo?.rubber_plan?.id || 0,
				rubber_plan_releases: cardInfo?.rubber_plan?.rubber_plan_releases?.length
					? cardInfo?.rubber_plan?.rubber_plan_releases
					: new Array(3).fill({ rubber_plan_id: cardInfo?.rubber_plan.id }),
				rubber_plan_returns: cardInfo?.rubber_plan?.rubber_plan_returns?.length
					? cardInfo?.rubber_plan?.rubber_plan_returns
					: new Array(2).fill({ rubber_plan_id: cardInfo?.rubber_plan.id }),
				rubber_tech_map_id: cardInfo?.rubber_plan?.rubber_tech_map_id || 0,
			},
			rubber_operations: cardInfo?.rubber_operations
				?.filter((operation) => !operation.is_default)
				?.map((operation) => ({
					...operation,
					name: operation.name ?? '',
				})),
			default_rubber_operations: cardInfo?.rubber_operations
				?.filter((operation) => operation.is_default)
				?.map((operation) => ({
					...operation,
					name: operation.name ?? '',
				})),
			rubber_sgd_out: sgdOutField(cardInfo?.rubber_sgd_out?.length || 0),
		}),
		[cardInfo],
	);

	const formConfig = useForm<RubberTechMapFormData>({
		// Коммент оставлен на случай тестирвоания валидации
		// resolver: (...args) => {
		// 	const res = yupResolver(isCreationMode ? creationValidationScheme : preparedEditValidationScheme)(
		// 		...args,
		// 	).then((res) => {
		// 		console.debug(res);
		// 		return res;
		// 	});

		// 	return res;
		// },
		resolver: yupResolver(isCreationMode ? creationValidationScheme : preparedEditValidationScheme),
		values: initialValues,
		mode: 'all',
	});

	return formConfig;
};
